/**
 * Theme sync: 2026-09-03-base
 * jrs-v2-method-restrictions.js — Per-position application-method rules
 *
 * Ports the live legacy bundle method-hiding rules (from
 * snippets/step1-bundle-inner-steps.liquid) into the v2 popup so the
 * application_method step only ever offers the methods a position
 * actually supports. Pure logic, no DOM, no state — callers pass in
 * everything (product tags, bundle slot, variant ids, base methods).
 *
 * Rule sources (authoritative spec: Doc 7 §4.3-4.4, Doc 8 §4.1):
 *   1. Product tag `hide-embroidery` → print only (every position)
 *   2. Product tag `hide-print`      → embroidery only (every position)
 *   3. Mix-and-match bundle (`bundle` + `mix&match`/`mix & match`) — per-step
 *      rule on large_back / large_front. Re-based 2026-08-01 on the CONTROL
 *      theme's current rules (step1-bundle-inner-steps.liquid, incl. its
 *      2026-07-24 ALLOW_LARGE_BACK_EMBROIDERY master switch — mirrored as a
 *      constant at the top of this module):
 *        - slots 1-2, large_back  → BOTH while the master switch is true
 *                                   (print only when flipped false)
 *        - slots 1-2, large_front → embroidery disabled (print only)
 *        - slot 3 (jackets / outerwear), both large positions
 *                                 → print disabled (embroidery only, Doc 8 §4.1)
 *        - slot >= 4              → no restriction (headwear has its own flow)
 *      The garment's OWN tags (rules 1-2, 4-5) still apply ON TOP, so a step-2
 *      garment tagged hide-print is still embroidery-only. When the slot is
 *      UNKNOWN (bundleSlot=null — bundle-wide has no per-step context), NO slot
 *      rule is applied: per-garment tags + the per-child union (bundle_wide) drive
 *      the methods. A KNOWN slot always applies its step rule.
 *   4. Variant presence: a position whose method has no variant id
 *      (embroideryVariantId / printVariantId === 0) drops that method.
 *   5. Per-position removal tags `remove-{position}-embroidery` /
 *      `remove-{position}-print` — the per-garment signal the single-product
 *      PDP already reads (snippets/step1.liquid). Hides that ONE method for
 *      that ONE position (e.g. a polo that is print-only on Large Back but
 *      still embroiderable on Left Breast). This is the signal the bundle-wide
 *      per-child resolver uses to apply each garment's own restriction.
 *
 * Safety: getAllowedMethods never returns an empty array — if the rules
 * would eliminate every method it falls back to baseMethods, so a
 * misconfigured product can never render a position with zero choices.
 *
 * resolveEffectiveMethod / resolveCategoryMethod (D1/D2b): given a child
 * product's OWN tags (D2d — the existing whole-product `hide-embroidery` /
 * `hide-print` restriction, rules 1-2 above; the same tags both PDPs already
 * read) + the customer's desired bundle-wide method, return the method that
 * child/category actually gets plus a `forced` flag (true when the desired
 * method isn't allowed and the single allowed method is substituted). The
 * bundle_wide emission path (jrs-v2-cart-submit.js) resolves each child through
 * these so a mixed bundle applies each product's own restriction automatically.
 */

window.JRS = window.JRS || {};

(function (JRS) {
  'use strict';

  var BOTH = ['embroidery', 'print'];

  /**
   * ── Large Back embroidery availability — CONTROL-THEME PARITY (2026-08-01) ──
   * Mirrors the master switch in the control theme's
   * step1-bundle-inner-steps.liquid (ALLOW_LARGE_BACK_EMBROIDERY, added
   * 2026-07-24, t-shirt exception lifted 2026-07-28):
   *   true  → Large Back embroidery is available on EVERY mix&match bundle
   *           slot (slots 1-2 previously print-only for production cost).
   *   false → previous behaviour (slots 1-2 hide embroidery on Large Back).
   * Scope: Large Back only. Large Front stays print-only on slots 1-2 and the
   * slot-3 print lock-out (Doc 8 §4.1) is unchanged, exactly like control.
   */
  var ALLOW_LARGE_BACK_EMBROIDERY = true;

  function _normalizeTags(productTags) {
    var out = [];
    if (!productTags || !productTags.length) return out;
    for (var i = 0; i < productTags.length; i++) {
      out.push(String(productTags[i]).trim().toLowerCase());
    }
    return out;
  }

  function _hasTag(tags, candidate) {
    return tags.indexOf(String(candidate).toLowerCase()) !== -1;
  }

  function _without(list, value) {
    var out = [];
    for (var i = 0; i < list.length; i++) {
      if (list[i] !== value) out.push(list[i]);
    }
    return out;
  }

  // Legacy checks `position.title | handle == 'large-back' | 'large-front'`.
  // v2 position ids use underscores (`large_back`). Normalise both so a
  // hyphenated handle or an underscored id resolves the same.
  function _normalisePositionId(positionId) {
    return String(positionId || '').toLowerCase().replace(/-/g, '_');
  }

  function _isLargePosition(positionId) {
    var n = _normalisePositionId(positionId);
    return n === 'large_back' || n === 'large_front';
  }

  // Intersection preserving the order of `a` (subset of `a`, never adds). Used
  // by resolveCategoryMethod to combine multiple children's allowed sets.
  function _intersect(a, b) {
    var out = [];
    for (var i = 0; i < a.length; i++) {
      if (b.indexOf(a[i]) !== -1) out.push(a[i]);
    }
    return out;
  }

  /**
   * Per-position, per-method removal tags — the authoritative PER-GARMENT
   * signal the single-product PDP already reads (snippets/step1.liquid): a
   * product tag `remove-{position-handle}-embroidery` or
   * `remove-{position-handle}-print` hides that ONE method for that ONE
   * position (unlike whole-product `hide-*`, which hides a method everywhere).
   * This is what lets a polo be embroidery-on-Left-Breast but print-only on
   * Large Back. The position part is matched across `-`/`_`/space separators so
   * a tag's `large-back` reconciles with the catalogue id `large_back`.
   *
   * @param {string[]} productTags
   * @param {string} positionId
   * @returns {{removePrint: boolean, removeEmbroidery: boolean}}
   */
  function _readPositionRemovalTags(productTags, positionId) {
    var out = { removePrint: false, removeEmbroidery: false };
    var tags = _normalizeTags(productTags);
    if (!tags.length || !positionId) return out;
    var pid = String(positionId).toLowerCase().replace(/[-\s]+/g, '_');
    for (var i = 0; i < tags.length; i++) {
      var t = tags[i];
      if (t.indexOf('remove') !== 0) continue;
      var method = null;
      var body = null;
      if (t.length > 11 && t.slice(-11) === '-embroidery') { method = 'embroidery'; body = t.slice(0, -11); }
      else if (t.length > 6 && t.slice(-6) === '-print') { method = 'print'; body = t.slice(0, -6); }
      else continue; // a `remove {position}` (whole-position) tag has no method suffix
      // Strip the leading `remove` + separator, normalise the position part.
      var pos = body.replace(/^remove[-\s]+/, '').replace(/[-\s]+/g, '_');
      if (pos !== pid) continue;
      if (method === 'print') out.removePrint = true;
      else out.removeEmbroidery = true;
    }
    return out;
  }

  /**
   * True when the bundle parent product carries BOTH the `bundle` tag
   * and a mix-and-match tag (`mix&match` or `mix & match`). Mirrors
   * step1-bundle-inner-steps.liquid lines 5-16.
   *
   * @param {string[]} productTags
   * @returns {boolean}
   */
  function isMixAndMatchBundle(productTags) {
    var tags = _normalizeTags(productTags);
    var hasBundle = _hasTag(tags, 'bundle');
    var hasMix = _hasTag(tags, 'mix&match') || _hasTag(tags, 'mix & match');
    return hasBundle && hasMix;
  }

  /**
   * Resolve the methods a single position is allowed to offer.
   *
   * @param {Object} opts
   * @param {string} opts.positionId            - catalogue id, e.g. 'large_back'
   * @param {string[]} [opts.productTags]       - bundle/product tags (JRS_PDP_CONTEXT.productTags)
   * @param {number|null} [opts.bundleSlot]     - 1-based bundle slot (categoryIndex + 1); null for multistep/unknown
   * @param {boolean} [opts.mixAndMatch]        - true when the bundle PARENT carries the
   *          mix&match tags. Lets slot rules engage on calls that pass a CHILD
   *          garment's tags (bundle_wide per-child resolution) — the mix&match
   *          tags live on the parent, never the child, and mixing parent tags
   *          into productTags would wrongly apply parent hide-* to children.
   * @param {number} [opts.embroideryVariantId] - 0 (or absent) drops embroidery
   * @param {number} [opts.printVariantId]      - 0 (or absent) drops print
   * @param {string[]} [opts.baseMethods]       - starting set; defaults to both
   * @returns {string[]} subset of ['embroidery','print'] (never empty)
   */
  function getAllowedMethods(opts) {
    opts = opts || {};
    var allowed = _computeAllowed(opts);
    // Never strand a position with zero methods: a caller that offers the position
    // anyway (application_method step) must have SOMETHING to pick. Callers that
    // decide VISIBILITY (position picker) use getAllowedMethodsRaw instead, so they
    // can HIDE a position whose methods are all removed rather than re-offer both.
    if (allowed.length) return allowed;
    return (opts.baseMethods && opts.baseMethods.length) ? opts.baseMethods.slice() : BOTH.slice();
  }

  /**
   * The TRULY-available methods for a position — same rules as getAllowedMethods
   * but WITHOUT the never-empty fallback. Returns [] when hide-* / remove-* /
   * missing-variant rules remove every method (e.g. a softshell tagged hide-print
   * AND remove-right-sleeve-embroidery → Right Sleeve has neither). The position
   * picker uses this to HIDE such a position — the PDP behaviour the merchant's
   * tags intend — instead of showing it with both methods (the fallback). Never
   * zeroed by the mix&match slot rule (a preference applied only if a method
   * survives), so [] means a genuine hard restriction.
   *
   * @param {Object} opts - same shape as getAllowedMethods
   * @returns {string[]} subset of ['embroidery','print'] (MAY be empty)
   */
  function getAllowedMethodsRaw(opts) {
    return _computeAllowed(opts || {});
  }

  // Shared computation for getAllowedMethods (+ its fallback) and
  // getAllowedMethodsRaw (no fallback). Returns the allowed set, possibly empty.
  function _computeAllowed(opts) {
    opts = opts || {};
    var positionId = opts.positionId;
    var tags = _normalizeTags(opts.productTags);
    var slot = (typeof opts.bundleSlot === 'number') ? opts.bundleSlot : null;
    var base = (opts.baseMethods && opts.baseMethods.length)
      ? opts.baseMethods.slice()
      : BOTH.slice();

    var allowed = base.slice();

    // 1-2. Product-level tag overrides (all positions, all flows).
    if (_hasTag(tags, 'hide-embroidery')) allowed = _without(allowed, 'embroidery');
    if (_hasTag(tags, 'hide-print')) allowed = _without(allowed, 'print');

    // 3. Mix-and-match slot lock-outs on large placements (large_back /
    //    large_front) — DECIDED here but APPLIED LAST (below). It is a bundle-
    //    composition PREFERENCE and must yield to the garment's own hard
    //    restrictions (hide-* / remove-* / missing variant): a hide-print garment
    //    on a print-only slot can only embroider, so the slot rule must not strip
    //    its last surviving method. A known slot drives the exact rule; an unknown
    //    slot (bundle-wide / merged view) falls back to the slot 1-2 rule (print
    //    only) — see header note.
    var slotRemoval = null;
    var mixMatch = opts.mixAndMatch === true || isMixAndMatchBundle(opts.productTags);
    if (_isLargePosition(positionId) && mixMatch) {
      var isLargeBack = _normalisePositionId(positionId) === 'large_back';
      if (slot === 3) {
        slotRemoval = 'print';       // slot 3 (jackets/outerwear) → embroidery only (Doc 8 §4.1)
      } else if (slot === 1 || slot === 2) {
        // Control-theme parity (2026-08-01, step1-bundle-inner-steps.liquid):
        //   large_front slots 1-2 → print only (embroidery disabled), unchanged.
        //   large_back  slots 1-2 → BOTH while ALLOW_LARGE_BACK_EMBROIDERY is
        //     true (control's 2026-07-24 master switch); flipping it false
        //     restores the old print-only rule.
        if (!isLargeBack) {
          slotRemoval = 'embroidery';
        } else if (!ALLOW_LARGE_BACK_EMBROIDERY) {
          slotRemoval = 'embroidery';
        }
      }
      // The garment's own hide-* / remove-* tags (rules 1-2, 4-5) still apply on
      // top. An UNKNOWN slot (bundleSlot=null — bundle-wide has no per-step
      // context) applies no slot rule: per-garment tags + the per-child union
      // drive it. slot >= 4: none.
    }

    // 4. Variant presence. Idempotent with the position-picker's own
    //    variant-id derivation; harmless to re-apply here.
    if (typeof opts.printVariantId === 'number' && opts.printVariantId === 0) {
      allowed = _without(allowed, 'print');
    }
    if (typeof opts.embroideryVariantId === 'number' && opts.embroideryVariantId === 0) {
      allowed = _without(allowed, 'embroidery');
    }

    // 5. Per-position, per-method removal tags (`remove-{position}-embroidery`
    //    / `remove-{position}-print`) — the per-garment signal the single-
    //    product PDP reads. Hides a method for THIS position only; this is what
    //    resolves a bundle child's Large Back independently (polo/sweatshirt =
    //    print-only, softshell = embroidery-only) from the child's own tags.
    var removal = _readPositionRemovalTags(opts.productTags, positionId);
    if (removal.removePrint) allowed = _without(allowed, 'print');
    if (removal.removeEmbroidery) allowed = _without(allowed, 'embroidery');

    // 3 (applied). Apply the mix&match slot preference now — AFTER the garment's
    // hard restrictions — and ONLY if a method survives. Otherwise a hide-print
    // (or remove-*) garment on a print-only slot would be stripped to zero and
    // the "never strand" fallback below would wrongly re-offer BOTH methods (the
    // reported per_product Large Back bug: hide-print sweatshirt still showed Print).
    if (slotRemoval) {
      var afterSlot = _without(allowed, slotRemoval);
      if (afterSlot.length) allowed = afterSlot;
    }

    // Raw set — MAY be empty. getAllowedMethods applies the never-empty fallback;
    // getAllowedMethodsRaw returns this as-is so the picker can hide the position.
    return allowed;
  }

  /**
   * Convenience predicate for guards (cart-submit): is `method` allowed
   * for this position under the given context?
   *
   * @param {string} method - 'embroidery' | 'print'
   * @param {Object} opts   - same shape as getAllowedMethods
   * @returns {boolean}
   */
  function isMethodAllowed(method, opts) {
    return getAllowedMethods(opts).indexOf(method) !== -1;
  }

  /**
   * Resolve the application method a single child product actually gets, given
   * the customer's bundle-wide desired method. Reuses getAllowedMethods (the
   * single source of truth) with the child's own tags, so per-child method
   * tags (rule 5) + whole-product hide-* tags apply per child. bundleSlot is
   * null — bundle_wide has no per-category slot; tag + variant rules only.
   *
   * @param {string[]} childTags        - the child product's tags
   * @param {string} positionId         - catalogue id, e.g. 'large_back'
   * @param {string} desiredMethod      - 'embroidery' | 'print' (bundle-wide choice)
   * @param {{embroidery?:number, print?:number}} variantIds - per-method variant ids
   *          from the active descriptor (shop-level in bundle_wide)
   * @param {string[]} [baseMethods]    - descriptor.application_methods (pre-restriction set)
   * @returns {{ method: string, forced: boolean, allowed: string[] }}
   *
   * `forced` is true only when desiredMethod isn't allowed for this child and
   * the single allowed method is substituted (D3 renders the "print isn't
   * available on this fabric" note off this flag).
   *
   * D1 SCOPE NOTE: getAllowedMethods never returns empty, so the "position not
   * offered on this child at all" case is NOT handled here — detecting a
   * position a child physically lacks needs per-child POSITION lists (separate
   * plumbing, tied to the open "unmatched logo positions" item). Hamza's rule
   * (tees / sweatshirts / outerwear all have a Large Back) never triggers it,
   * so D1 assumes the position exists on each child.
   */
  function resolveEffectiveMethod(childTags, positionId, desiredMethod, variantIds, baseMethods, slotCtx) {
    variantIds = variantIds || {};
    slotCtx = slotCtx || {};
    var allowed = getAllowedMethods({
      positionId: positionId,
      productTags: childTags,
      // Slot context (2026-08-01, control parity): bundle_wide children DO sit
      // in known slots (child index + 1). Callers pass {bundleSlot, mixAndMatch}
      // so the mix&match slot rules apply per garment exactly as the control
      // theme applies them per bundle step. Omitted → null (legacy behaviour).
      bundleSlot: (typeof slotCtx.bundleSlot === 'number') ? slotCtx.bundleSlot : null,
      mixAndMatch: slotCtx.mixAndMatch === true,
      embroideryVariantId: variantIds.embroidery,
      printVariantId: variantIds.print,
      baseMethods: baseMethods
    });
    // allowed.length >= 1 guaranteed by getAllowedMethods.
    if (allowed.indexOf(desiredMethod) !== -1) {
      return { method: desiredMethod, forced: false, allowed: allowed };
    }
    return { method: allowed[0], forced: true, allowed: allowed };
  }

  /**
   * Resolve ONE application method for a whole bundle category at a position,
   * given the customer's bundle-wide desired method. A category may hold more
   * than one child; the category can only use a method EVERY child in it
   * supports, so the allowed set is the intersection of each child's allowed
   * methods (D2b). In the bundle_wide ProductConfiguration a category holds a
   * single child, so the intersection is that child's set and `conflict` never
   * fires — but the intersection keeps this correct if a category ever groups
   * multiple children.
   *
   * @param {Array<{tags: string[]}>} children - the category's children
   * @param {string} positionId
   * @param {string} desiredMethod - 'embroidery' | 'print'
   * @param {{embroidery?:number, print?:number}} variantIds - per-method variant ids
   * @param {string[]} [baseMethods] - descriptor.application_methods
   * @returns {{ method: string, forced: boolean, conflict: boolean }}
   *   conflict=true → the category's children have NO common method (e.g. a
   *   print-only + an embroidery-only child in one category). Out of D2b scope:
   *   caller falls back to desiredMethod (no worse than today) + a diagnostic.
   */
  function resolveCategoryMethod(children, positionId, desiredMethod, variantIds, baseMethods, slotCtx) {
    variantIds = variantIds || {};
    slotCtx = slotCtx || {};
    var list = children || [];
    // No children for this category → no restriction data; honour desired.
    if (!list.length) return { method: desiredMethod, forced: false, conflict: false };

    var categoryAllowed = null; // null = not yet seeded
    for (var i = 0; i < list.length; i++) {
      var childAllowed = getAllowedMethods({
        positionId: positionId,
        productTags: (list[i] && list[i].tags) || [],
        // Slot context (2026-08-01, control parity) — see resolveEffectiveMethod.
        bundleSlot: (typeof slotCtx.bundleSlot === 'number') ? slotCtx.bundleSlot : null,
        mixAndMatch: slotCtx.mixAndMatch === true,
        embroideryVariantId: variantIds.embroidery,
        printVariantId: variantIds.print,
        baseMethods: baseMethods
      });
      categoryAllowed = (categoryAllowed === null)
        ? childAllowed
        : _intersect(categoryAllowed, childAllowed);
    }

    if (!categoryAllowed || categoryAllowed.length === 0) {
      // Intra-category conflict — no method works for every child.
      return { method: desiredMethod, forced: false, conflict: true };
    }
    if (categoryAllowed.indexOf(desiredMethod) !== -1) {
      return { method: desiredMethod, forced: false, conflict: false };
    }
    return { method: categoryAllowed[0], forced: true, conflict: false };
  }

  JRS.MethodRestrictions = {
    isMixAndMatchBundle: isMixAndMatchBundle,
    getAllowedMethods: getAllowedMethods,
    // TRULY-available methods (no never-empty fallback) — [] when every method is
    // removed. The position picker hides a position when this is empty.
    getAllowedMethodsRaw: getAllowedMethodsRaw,
    isMethodAllowed: isMethodAllowed,
    resolveEffectiveMethod: resolveEffectiveMethod,
    resolveCategoryMethod: resolveCategoryMethod
  };
})(window.JRS);
